import {
  Plan2PlateDrawer,
  Plan2PlateDrawerHeader,
  usePlan2PlateDisclosure,
} from "./Plan2PlateDrawer";
import React, { useContext, useEffect, useState } from "react";
import { SearchInput } from "./SearchInput";
import { SearchResultCard } from "./SearchResultCard";
import { CreateFoodDrawer } from "./CreateFoodDrawer";
import { AddButton } from "./buttons/AddButton";
import Food from "../data/food";
import { PurchaseEditor } from "./PurchaseEditor";
import { initialPurchase, Purchase } from "../data/purchase";
import { updatePurchaseWithFood } from "../utils/purchaseUtils";
import InfiniteScroll from "react-infinite-scroll-component";
import { useImmer } from "use-immer";
import { usePerformSearch } from "../utils/searchUtils";
import { searchFood } from "../api";
import { useNutriSlider } from "./nutriSlider";
import { DrawerHistoryContext } from "../contexts";
import { formatFoodDescription } from "../utils/foodUtils";
import { queryKeys } from "../queries/queries";
import { useQueryClient } from "@tanstack/react-query";
import { invalidatePurchases, seedPurchase } from "../queries/caches";

export function CreatePurchaseDrawer({
  onClose,
  onSubmit,
  open,
  food,
}: {
  onClose: () => void;
  open: boolean;
  onSubmit: (p: Purchase) => void;
  food?: Food;
}) {
  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const {
    isOpen: isOpenCreateFood,
    onOpen: onOpenCreateFood,
    onClose: onCloseCreateFood,
  } = usePlan2PlateDisclosure(drawerHistoryContext);

  useEffect(() => {
    if (!open) {
      setSelectedFood(food);
      setSearchTerm("");
      updateSearchResults([]);
      setSearchResultCount(0);
    } else {
      setSelectedFood(food);
    }
  }, [open, food]);

  const [selectedFood, setSelectedFood] = useState<Food>();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, updateSearchResults] = useImmer<Food[]>([]);
  const [searchResultCount, setSearchResultCount] = useState(0);
  const queryClient = useQueryClient();

  const nutriSliderProps = useNutriSlider("food");

  const { performSearch, fetchMoreData } = usePerformSearch(
    undefined,
    nutriSliderProps,
    searchTerm,
    searchResults,
    updateSearchResults,
    setSearchResultCount,
    searchFood,
  );

  return (
    <>
      <CreateFoodDrawer
        onClose={onCloseCreateFood}
        open={isOpenCreateFood}
        onSubmit={setSelectedFood}
      />
      <Plan2PlateDrawer
        onClose={onClose}
        open={open}
        header={<Plan2PlateDrawerHeader heading={"Produkt erstellen"} />}
        noScroll
        body={
          <>
            {!selectedFood ? (
              <>
                <div className="flex w-full items-center gap-2">
                  <SearchInput
                    value={searchTerm}
                    performSearch={performSearch}
                    setSearchTerm={setSearchTerm}
                    placeholder={"Suche nach Lebensmittel"}
                    dataCy="purchase-food-search-input"
                  />
                  <AddButton onClick={onOpenCreateFood} />
                </div>
                <div
                  id={"create-purchase-scroll-stack"}
                  className="flex w-full flex-col items-stretch overflow-auto"
                >
                  <InfiniteScroll
                    scrollableTarget={"create-purchase-scroll-stack"}
                    dataLength={searchResults.length}
                    next={fetchMoreData}
                    hasMore={searchResults.length < searchResultCount}
                    loader={<h4>Loading...</h4>}
                    style={{ width: "100%" }}
                  >
                    <div className="flex w-full flex-wrap">
                      {searchResults.map((searchResult) => (
                        <SearchResultCard
                          key={1}
                          onClick={() => setSelectedFood(searchResult)}
                        >
                          {searchResult.description}
                        </SearchResultCard>
                      ))}
                    </div>
                  </InfiniteScroll>
                </div>
              </>
            ) : (
              <>
                <span className="w-fit rounded-pill bg-surface px-2.5 py-0.5 text-xs font-medium text-ink border border-line">
                  {formatFoodDescription(selectedFood)}
                </span>
                <PurchaseEditor
                  initialPurchase={updatePurchaseWithFood(
                    initialPurchase(selectedFood.id),
                    selectedFood,
                  )}
                  isCreate={true}
                  food={selectedFood}
                  afterSave={async (p) => {
                    seedPurchase(queryClient, p);
                    await invalidatePurchases(queryClient);
                    onClose();
                    onSubmit(p);
                  }}
                />
              </>
            )}
          </>
        }
      />
    </>
  );
}
